import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Rectangle,
  type RectangleProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Paper, Typography, useTheme } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";

type ClientPoint = {
  client_name: string;
  submitted_count: number;
  not_submitted_count: number;
  total_count: number;
};

type Props = {
  data: ClientPoint[];
  isFullscreen: boolean;
};

type SeriesKey = "submitted" | "not";

const ClientTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: ClientPoint }[] }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const pct = p.total_count ? ((p.submitted_count / p.total_count) * 100).toFixed(0) : "0";
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{p.client_name}</Typography>
      <Typography variant="body2">
        Submitted: <strong>{p.submitted_count.toLocaleString()}</strong> of {p.total_count.toLocaleString()} ({pct}%)
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Not submitted: {p.not_submitted_count.toLocaleString()}
      </Typography>
    </Paper>
  );
};

const truncate = (s: string, n = 16) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * Per-client Aura submission breakdown — vertical stacked columns (submitted vs
 * not submitted), top 15 clients by claim volume. Each segment emphasizes
 * INDEPENDENTLY on hover (per-bar onMouseEnter + custom shape, since recharts'
 * activeBar activates by category, not by stacked segment).
 */
const SubmissionByClientChart = ({ data, isFullscreen }: Props) => {
  const palette = useChartPalette();
  const theme = useTheme();
  const [hover, setHover] = useState<{ key: SeriesKey; index: number } | null>(null);
  if (!data.length) return <EmptyChartState />;

  const SegmentShape = (props: RectangleProps & { index?: number; seriesKey?: SeriesKey }) => {
    const isActive = hover?.key === props.seriesKey && hover?.index === props.index;
    return (
      <Rectangle
        {...props}
        fillOpacity={isActive ? 1 : 0.92}
        stroke={isActive ? theme.palette.background.paper : "none"}
        strokeWidth={isActive ? 2 : 0}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
        <defs>
          <linearGradient id="submByFillSubmitted" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette[0]} stopOpacity={0.95} />
            <stop offset="100%" stopColor={palette[0]} stopOpacity={0.55} />
          </linearGradient>
          <linearGradient id="submByFillNot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.palette.text.disabled} stopOpacity={0.85} />
            <stop offset="100%" stopColor={theme.palette.text.disabled} stopOpacity={0.4} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
        <XAxis
          dataKey="client_name"
          angle={-30}
          textAnchor="end"
          interval={0}
          height={70}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickFormatter={(v: string) => truncate(v)}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        />
        <Tooltip
          cursor={false}
          content={(props) => (
            <ClientTooltip
              active={props.active}
              payload={props.payload as unknown as { payload: ClientPoint }[] | undefined}
            />
          )}
        />
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          payload={[
            { value: "Submitted to Aura", type: "square", id: "submitted", color: palette[0] },
            { value: "Not Submitted", type: "square", id: "not", color: theme.palette.text.disabled },
          ]}
        />
        <Bar
          dataKey="submitted_count"
          name="Submitted to Aura"
          stackId="a"
          fill="url(#submByFillSubmitted)"
          maxBarSize={52}
          isAnimationActive={false}
          shape={<SegmentShape seriesKey="submitted" />}
          onMouseEnter={(_, index) => setHover({ key: "submitted", index })}
          onMouseLeave={() => setHover(null)}
        />
        <Bar
          dataKey="not_submitted_count"
          name="Not Submitted"
          stackId="a"
          fill="url(#submByFillNot)"
          maxBarSize={52}
          radius={[6, 6, 0, 0]}
          isAnimationActive={false}
          shape={<SegmentShape seriesKey="not" />}
          onMouseEnter={(_, index) => setHover({ key: "not", index })}
          onMouseLeave={() => setHover(null)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SubmissionByClientChart;
